import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shared "Complete the look" cross-sell logic — the single source of truth
 * for how we resolve matching JEWELLERY for a given clothing item. Used by
 * both the PDP rail (CompleteTheLook.jsx) and the add-to-bag cross-sell modal
 * (AddedCrossSellModal.jsx) so their match strategy can never drift apart.
 *
 * NOTE: extracted verbatim from CompleteTheLook's original inline logic — no
 * behavioural change. CompleteTheLook now imports these helpers.
 */

/** Resolve an image url the same way the PDP rail does (absolute pass-through,
 *  `/api/…` prefixed with the backend origin) so images never 404 cross-origin. */
export const imgSrc = (url) => {
    if (!url) return "/default-item.svg";
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
};

/** The occasion/colour we try to match jewellery on, pulled off a clothing item. */
export const matchOccasion = (item) => item?.occasion || item?.occasions?.[0] || "";
export const matchColor = (item) => item?.color || item?.colour_families?.[0] || "";

/**
 * Fetch jewellery matched to a garment's occasion + colour, falling back to
 * any live jewellery. Excludes `excludeId` and out-of-stock, caps at 6.
 *
 * Assumes the caller has already confirmed the jewellery department is LIVE
 * (via useDepartments) — /shop/items already excludes non-live departments,
 * but callers gate on it explicitly to skip the fetch entirely pre-launch.
 *
 * @returns {Promise<Array>} cleaned jewellery items (0–6)
 */
export function fetchMatchingJewellery(item, { excludeId = null } = {}) {
    const occasion = matchOccasion(item);
    const color = matchColor(item);

    const clean = (arr) =>
        (Array.isArray(arr) ? arr : [])
            .filter((j) => j.id !== excludeId && Number(j.quantity) > 0)
            .slice(0, 6);

    const base = { department: "jewellery", limit: 8 };
    const matchParams = { ...base };
    if (occasion) matchParams.occasion = occasion;
    if (color) matchParams.color = color;

    const fetchMatched = (occasion || color)
        ? axios.get(`${API}/shop/items`, { params: matchParams }).then((r) => r.data)
        : Promise.resolve([]);

    return fetchMatched
        .then((matched) => {
            const cleaned = clean(matched);
            if (cleaned.length > 0) return cleaned;
            // Fallback — any live jewellery.
            return axios
                .get(`${API}/shop/items`, { params: base })
                .then((r) => clean(r.data));
        })
        .catch(() => []);
}
